import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';

const AuthContext = createContext(null);
const API_BASE = '/api/v1';

const originalFetch = window.fetch;

let _refreshPromise = null;
let _sessionExpiredShown = false;

function clearRefreshLock() {
  _refreshPromise = null;
}

let _authErrSent = 0, _authErrWindowStart = 0;
const AUTH_ERR_MAX = 5, AUTH_ERR_WINDOW_MS = 60000;
function captureAuthError(err, url, status) {
  const now = Date.now();
  if (now - _authErrWindowStart > AUTH_ERR_WINDOW_MS) { _authErrWindowStart = now; _authErrSent = 0; }
  if (_authErrSent >= AUTH_ERR_MAX) return;
  _authErrSent += 1;
  try {
    Sentry.captureException(err, { extra: { url, status } });
  } catch (e) { /* reporting must never break auth flow */ }
}

function getRefreshToken() {
  return localStorage.getItem('synoti_refresh_token') || sessionStorage.getItem('synoti_refresh_token');
}

function clearRefreshToken() {
  localStorage.removeItem('synoti_refresh_token');
  sessionStorage.removeItem('synoti_refresh_token');
}

async function doRefresh() {
  const refreshTok = getRefreshToken();
  if (!refreshTok) return null;
  // A background refresh must never reject: the shared _refreshPromise is awaited by the
  // fetch wrapper, every concurrent 401 handler and the 25-minute interval, so a network
  // blip here surfaced as an unhandled "TypeError: Failed to fetch" instead of a plain
  // "refresh failed". Returning null lets the existing session-expired path run.
  try {
    const refreshRes = await originalFetch.call(window, `${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshTok }),
    });
    if (refreshRes.ok) {
      const data = await refreshRes.json();
      localStorage.setItem('synoti_access_token', data.access_token);
      const storage = localStorage.getItem('synoti_refresh_token') ? localStorage : sessionStorage;
      storage.setItem('synoti_refresh_token', data.refresh_token);
      window.dispatchEvent(new CustomEvent('synoti_token_refreshed', { detail: data }));
      return data.access_token;
    }
  } catch (err) {
    captureAuthError(err, `${API_BASE}/auth/refresh`, null);
  }
  return null;
}

function handleSessionExpired() {
  if (_sessionExpiredShown) return;
  _sessionExpiredShown = true;
  localStorage.removeItem('synoti_access_token');
  clearRefreshToken();
  window.dispatchEvent(new CustomEvent('synoti_session_expired'));
  window.location.hash = 'login';
}

window.fetch = async function(url, options = {}) {
  if (typeof url !== 'string' || !url.startsWith('/api/')) {
    return originalFetch.call(window, url, options);
  }
  const token = localStorage.getItem('synoti_access_token');
  if (token) {
    options = {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    };
  }
  let res;
  try {
    res = await originalFetch.call(window, url, options);
  } catch {
    throw new Error(`Network unreachable`);
  }
  if (res.status >= 502) return res;
  if (res.status === 401 && token) {
    if (!_refreshPromise) {
      _refreshPromise = doRefresh().finally(clearRefreshLock);
    }
    const newToken = await _refreshPromise;
    if (newToken) {
      options.headers['Authorization'] = `Bearer ${newToken}`;
      res = await originalFetch.call(window, url, options);
    } else {
      handleSessionExpired();
    }
  }
  return res;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('synoti_access_token'));

  const logout = useCallback(() => {
    localStorage.removeItem('synoti_access_token');
    clearRefreshToken();
    setToken(null);
    setUser(null);
  }, []);

  const login = useCallback(async (username, password, tenantSlug, remember = true) => {
    let res;
    try {
      res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, tenant_slug: tenantSlug }),
      });
    } catch {
      captureAuthError(new Error('Network error during login'), `${API_BASE}/auth/login`, null);
      throw new Error('Cannot reach the server. Check your internet connection.');
    }
    if (!res.ok) {
      if (res.status >= 500) captureAuthError(new Error('Server error during login'), `${API_BASE}/auth/login`, res.status);
      let detail = 'Login failed';
      let remaining = null;
      let lockedSeconds = null;
      try {
        const data = await res.json();
        detail = data.detail || detail;
        if (detail && detail.includes('|')) {
          const parts = detail.split('|');
          detail = parts[0];
          for (const part of parts.slice(1)) {
            if (part.startsWith('remaining=')) remaining = parseInt(part.split('=')[1]);
            if (part.startsWith('locked_seconds=')) lockedSeconds = parseInt(part.split('=')[1]);
          }
        }
      } catch {
        detail = `Server error (${res.status}). Please try again.`;
      }
      const err = new Error(detail);
      err.status = res.status;
      err.remaining = remaining;
      err.lockedSeconds = lockedSeconds;
      throw err;
    }
    const data = await res.json();
    localStorage.setItem('synoti_access_token', data.access_token);
    if (remember) {
      localStorage.setItem('synoti_refresh_token', data.refresh_token);
      sessionStorage.removeItem('synoti_refresh_token');
    } else {
      sessionStorage.setItem('synoti_refresh_token', data.refresh_token);
      localStorage.removeItem('synoti_refresh_token');
    }
    setToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  const refreshToken = useCallback(async () => {
    const refreshTok = getRefreshToken();
    if (!refreshTok) {
      logout();
      return false;
    }
    if (_refreshPromise) {
      const newToken = await _refreshPromise;
      if (newToken) {
        setToken(newToken);
        return true;
      }
      return false;
    }
    _refreshPromise = doRefresh().finally(clearRefreshLock);
    const newToken = await _refreshPromise;
    if (newToken) {
      setToken(newToken);
      return true;
    }
    logout();
    return false;
  }, [logout]);

  useEffect(() => {
    const init = async () => {
      const stored = localStorage.getItem('synoti_access_token');
      if (!stored) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { 'Authorization': `Bearer ${stored}` },
        });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
        } else if (res.status === 401) {
          const refreshed = await refreshToken();
          if (refreshed) {
            const newToken = localStorage.getItem('synoti_access_token');
            const res2 = await fetch(`${API_BASE}/auth/me`, {
              headers: { 'Authorization': `Bearer ${newToken}` },
            });
            if (res2.ok) {
              setUser(await res2.json());
            }
          }
        } else {
          logout();
        }
      } catch (err) {
        captureAuthError(err, `${API_BASE}/auth/me`, null);
        logout();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [logout, refreshToken]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!token) return;
      // Background refresh: swallow failures so a blip never becomes an unhandled rejection.
      refreshToken().catch(() => {});
    }, 25 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, refreshToken]);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    let currentToken = localStorage.getItem('synoti_access_token');
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${currentToken}`,
    };
    let res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        currentToken = localStorage.getItem('synoti_access_token');
        headers['Authorization'] = `Bearer ${currentToken}`;
        res = await fetch(url, { ...options, headers });
      }
    }
    if (res.status >= 500) captureAuthError(new Error('Server error'), url, res.status);
    return res;
  }, [refreshToken]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshToken, fetchWithAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function requireAuth(WrappedComponent) {
  return function ProtectedRoute(props) {
    const { user, loading } = useAuth();
    if (loading) {
      return (
        <div className="flex items-center justify-center h-screen bg-gray-900">
          <div className="flex items-center gap-3 text-gray-400">
            <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Loading...
          </div>
        </div>
      );
    }
    if (!user) {
      window.location.hash = 'login';
      return null;
    }
    return <WrappedComponent {...props} />;
  };
}
